/* eslint-disable eslint-comments/disable-enable-pair   -- complex component */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- need this*/
import "./user-default-cell.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Popup } from "@tql/ui-components-react/kendo/popup";
import { Tooltip } from "@tql/ui-components-react/kendo/tooltip";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	markerPin_04,
	plus,
	route,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useMemo, useRef, useState } from "react";

type UserDefaultCellProps = {
	readonly isPickupDefault?: boolean;
	readonly isDeliveryDefault?: boolean;
	readonly hasPickupDetail?: boolean;
	readonly hasDeliveryDetail?: boolean;
	readonly onSetUserDefault: (type: "Pickup" | "Delivery") => void;
};

export const UserDefaultCell = ({
	isPickupDefault,
	isDeliveryDefault,
	hasPickupDetail,
	hasDeliveryDetail,
	onSetUserDefault,
}: Readonly<UserDefaultCellProps>) => {
	const [showPopup, setShowPopup] = useState(false);
	const anchorRef = useRef<HTMLDivElement>(null);

	const handleOpenPopup = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();
			setShowPopup(true);
		},
		[],
	);

	const handleClosePopup = useCallback(() => {
		setShowPopup(false);
	}, []);

	const handleSelect = useCallback(
		(type: "Pickup" | "Delivery") => {
			onSetUserDefault(type);
			handleClosePopup();
		},
		[onSetUserDefault, handleClosePopup],
	);

	const tooltipMessage = useMemo(() => {
		if (!hasPickupDetail && !hasDeliveryDetail) {
			return "Add pickup or delivery details to make this address a default.";
		}
		if (!hasPickupDetail) {
			return "Add pickup details to make this address a default.";
		}
		if (!hasDeliveryDetail) {
			return "Add delivery details to make this address a default.";
		}
		return undefined;
	}, [hasPickupDetail, hasDeliveryDetail]);

	const canAddPickup = !isPickupDefault && hasPickupDetail;
	const canAddDelivery = !isDeliveryDefault && hasDeliveryDetail;
	const isMissingDetails = !canAddPickup && !canAddDelivery;
	const hasAnyDefault = isPickupDefault || isDeliveryDefault;

	const isButtonDisabled = showPopup || isMissingDetails;

	return (
		<div
			className={
				hasAnyDefault ? "address-book-user-default-cell-active" : undefined
			}
		>
			{isPickupDefault ? (
				<Button startIcon={<SvgIcon icon={route} />} variant={"tertiary"}>
					Pickup
				</Button>
			) : null}
			{isDeliveryDefault ? (
				<Button
					startIcon={<SvgIcon icon={markerPin_04} />}
					variant={"tertiary"}
				>
					Delivery
				</Button>
			) : null}
			<Tooltip anchorElement={"target"} position={"top"}>
				<div
					ref={anchorRef}
					style={{ display: "inline-block" }}
					title={isMissingDetails ? tooltipMessage : undefined}
				>
					<Button
						disabled={isButtonDisabled}
						onClick={handleOpenPopup}
						startIcon={<SvgIcon icon={plus} />}
						variant={"tertiary"}
					/>
				</div>
			</Tooltip>
			{showPopup ? (
				<Popup
					anchor={anchorRef.current}
					onMouseDownOutside={handleClosePopup}
					popupClass={"popup-content"}
					show={showPopup}
				>
					<div className={"address-book-user-default-popup"}>
						{canAddPickup ? (
							<Button
								onClick={() => handleSelect("Pickup")}
								variant={"tertiary"}
							>
								Pickup
							</Button>
						) : null}
						{canAddDelivery ? (
							<Button
								onClick={() => handleSelect("Delivery")}
								variant={"tertiary"}
							>
								Delivery
							</Button>
						) : null}
					</div>
				</Popup>
			) : null}
		</div>
	);
};
